import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { TablePagination } from "@project/admin/components/table-pagination";
import { SearchableSelect } from "@project/admin/components/searchable-select";
import { userPermissionService } from "@project/services/admin";
import type {
  AdminUserRecord,
  AuthPermission,
} from "@project/types/user-permission";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Badge } from "@project/components/ui/badge";
import { Checkbox } from "@project/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@project/components/ui/tabs";
import { projectToast } from "@project/lib/toast";
import { getApiErrorMessage } from "@project/lib/api-error";
import { cn } from "@project/lib/utils";
import {
  Info,
  ListChecks,
  Loader2,
  RefreshCw,
  Search,
  Shield,
  ShieldCheck,
  UserCog,
  Users,
} from "lucide-react";

type TabKey = "users" | "assign" | "permissions";

type PermissionModule = {
  key: string;
  label: string;
  permissions: AuthPermission[];
};

const TH = "px-3 py-2.5 font-semibold text-foreground";
const STANDARD_PERMISSION = /^Can (add|change|delete|view) /i;

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** "Jivandhara | Admission Inquiry" → "Admission Inquiry"; bare model names fall back to the permission name. */
function getModule(permission: AuthPermission): { key: string; label: string } {
  const match = permission.codename.match(/^(add|change|delete|view)_(.+)$/);
  const contentType = permission.content_type?.trim() ?? "";
  const key = match?.[2] ?? (contentType || "other");

  if (contentType.includes("|")) {
    const label = contentType.split("|").pop()?.trim();
    if (label) {
      return { key, label: capitalize(label) };
    }
  }
  if (STANDARD_PERMISSION.test(permission.name)) {
    return {
      key,
      label: capitalize(permission.name.replace(STANDARD_PERMISSION, "")),
    };
  }
  return { key, label: capitalize(contentType || "Other") };
}

function buildModules(permissions: AuthPermission[]): PermissionModule[] {
  const map = new Map<string, PermissionModule>();
  for (const permission of permissions) {
    const { key, label } = getModule(permission);
    const mod = map.get(key) ?? { key, label, permissions: [] };
    mod.permissions.push(permission);
    map.set(key, mod);
  }
  const modules = [...map.values()];
  modules.forEach(m =>
    m.permissions.sort((a, b) => a.name.localeCompare(b.name))
  );
  return modules.sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
  );
}

function filterModules(
  modules: PermissionModule[],
  search: string
): PermissionModule[] {
  const term = search.trim().toLowerCase();
  if (!term) {
    return modules;
  }
  return modules
    .map(m =>
      m.label.toLowerCase().includes(term)
        ? m
        : {
            ...m,
            permissions: m.permissions.filter(
              p =>
                p.name.toLowerCase().includes(term) ||
                p.codename.toLowerCase().includes(term)
            ),
          }
    )
    .filter(m => m.permissions.length > 0);
}

function getUserName(user: AdminUserRecord): string {
  const full =
    user.full_name?.trim() ||
    [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  return full || user.email || "Unnamed user";
}

/** IDs of the user's currently assigned direct permissions (API sends objects). */
function getUserPermissionIds(user: AdminUserRecord): number[] {
  return (user.user_permissions ?? [])
    .map(p => (typeof p === "number" ? p : Number(p?.id)))
    .filter(id => Number.isFinite(id));
}

function getInitials(name: string): string {
  return (
    name
      .split(/[\s@.]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part[0]?.toUpperCase())
      .join("") || "U"
  );
}

function formatDate(value?: string | null): string {
  if (!value) {
    return "—";
  }
  // API sends "+0000" offsets; normalise to "+00:00" for reliable parsing.
  const date = new Date(value.replace(/([+-]\d{2})(\d{2})$/, "$1:$2"));
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
}

function matchesUser(user: AdminUserRecord, search: string): boolean {
  const term = search.trim().toLowerCase();
  if (!term) {
    return true;
  }
  return [getUserName(user), user.email, user.phone_number, user.user_type]
    .filter(Boolean)
    .some(value => String(value).toLowerCase().includes(term));
}

function SearchBox({
  value,
  onChange,
  placeholder,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  className?: string;
}) {
  return (
    <div className={cn("relative w-full sm:max-w-sm", className)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-muted/40 rounded-xl border-transparent pl-9"
      />
    </div>
  );
}

function CenterState({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-sm text-muted-foreground">
      {children}
    </div>
  );
}

function UserAvatar({ user }: { user: AdminUserRecord }) {
  const name = getUserName(user);
  return (
    <div className="flex items-center gap-3">
      <div className="bg-primary/10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-primary">
        {getInitials(name)}
      </div>
      <p className="truncate font-semibold text-foreground">{name}</p>
    </div>
  );
}

export default function UserPermissionsPage() {
  const [tab, setTab] = useState<TabKey>("users");

  const [users, setUsers] = useState<AdminUserRecord[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);

  const [permissions, setPermissions] = useState<AuthPermission[]>([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  const [dialogUser, setDialogUser] = useState<AdminUserRecord | null>(null);
  const [assignUserId, setAssignUserId] = useState("");

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      setUsers(await userPermissionService.listUsers());
    } catch (err) {
      setUsers([]);
      projectToast.error(getApiErrorMessage(err, "Could not load users"));
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const loadPermissions = useCallback(async () => {
    setPermissionsLoading(true);
    try {
      setPermissions(await userPermissionService.listPermissions());
    } catch (err) {
      projectToast.error(getApiErrorMessage(err, "Could not load permissions"));
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUsers();
    void loadPermissions();
  }, [loadUsers, loadPermissions]);

  const modules = useMemo(() => buildModules(permissions), [permissions]);

  const assignPermissions = async (
    user: AdminUserRecord,
    ids: number[]
  ): Promise<boolean> => {
    try {
      await userPermissionService.assignPermissions(user.id, ids);
      // Keep the list in sync so reopening this user shows the saved selection.
      const byId = new Map(permissions.map(p => [p.id, p]));
      setUsers(prev =>
        prev.map(u =>
          u.id === user.id
            ? { ...u, user_permissions: ids.map(id => byId.get(id) ?? id) }
            : u
        )
      );
      projectToast.success(`Permissions assigned to ${getUserName(user)}`);
      return true;
    } catch (err) {
      projectToast.error(
        getApiErrorMessage(err, "Could not assign permissions")
      );
      return false;
    }
  };

  const refresh = () => {
    if (tab === "permissions") {
      void loadPermissions();
    } else {
      void loadUsers();
    }
  };

  const assignUser = users.find(u => u.id === assignUserId) ?? null;

  return (
    <AdminShell
      title="User Role & Permission"
      subtitle="Manage users and control what each user can access"
      actions={
        <Button
          variant="outline"
          size="sm"
          className="gap-1 rounded-xl"
          onClick={refresh}
        >
          <RefreshCw
            className={cn(
              "h-4 w-4",
              (usersLoading || permissionsLoading) && "animate-spin"
            )}
          />
          Refresh
        </Button>
      }
    >
      <Tabs
        value={tab}
        onValueChange={v => setTab(v as TabKey)}
        variant="line"
        className="space-y-6"
      >
        <TabsList>
          <TabsTrigger value="users" className="shrink-0 gap-2">
            <Users className="h-4 w-4" />
            <span>User List</span>
          </TabsTrigger>
          <TabsTrigger value="assign" className="shrink-0 gap-2">
            <UserCog className="h-4 w-4" />
            <span>Assign Permissions</span>
          </TabsTrigger>
          <TabsTrigger value="permissions" className="shrink-0 gap-2">
            <ListChecks className="h-4 w-4" />
            <span>Permissions List</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-0 focus-visible:outline-none">
          <UsersTab
            users={users}
            loading={usersLoading}
            onAssign={setDialogUser}
          />
        </TabsContent>

        <TabsContent value="assign" className="mt-0 focus-visible:outline-none">
          <div className="space-y-4">
            <div className="rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-5">
              <div className="grid gap-4 md:grid-cols-[minmax(0,380px)_1fr] md:items-end">
                <div className="space-y-1.5">
                  <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    Select User
                  </p>
                  <SearchableSelect
                    value={assignUserId || undefined}
                    onValueChange={setAssignUserId}
                    options={users.map(u => ({
                      value: u.id,
                      label: getUserName(u),
                      secondary: u.email ?? u.phone_number ?? undefined,
                    }))}
                    placeholder={
                      usersLoading ? "Loading users…" : "Choose a user"
                    }
                    searchPlaceholder="Search by name or email..."
                    emptyText="No users found"
                    disabled={usersLoading}
                  />
                </div>
                {assignUser && (
                  <div className="bg-muted/30 flex flex-wrap items-center gap-3 rounded-xl border border-border px-4 py-2.5">
                    <UserAvatar user={assignUser} />
                    <div className="flex flex-wrap gap-2 sm:ml-auto">
                      {assignUser.email && (
                        <Badge variant="outline" className="font-normal">
                          {assignUser.email}
                        </Badge>
                      )}
                      {assignUser.phone_number && (
                        <Badge variant="outline" className="font-normal">
                          {assignUser.phone_number}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {assignUser ? (
              <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
                <PermissionAssigner
                  key={assignUser.id}
                  modules={modules}
                  loading={permissionsLoading}
                  initialIds={getUserPermissionIds(assignUser)}
                  onRetry={loadPermissions}
                  onSubmit={ids => assignPermissions(assignUser, ids)}
                  onCancel={() => setAssignUserId("")}
                  bodyClassName="max-h-[65vh]"
                />
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border bg-card px-4 py-16 text-center">
                <span className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-full">
                  <UserCog className="h-6 w-6 text-primary" />
                </span>
                <p className="font-semibold text-foreground">
                  No user selected
                </p>
                <p className="max-w-sm text-sm text-muted-foreground">
                  Select a user above to assign direct permissions.
                </p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent
          value="permissions"
          className="mt-0 focus-visible:outline-none"
        >
          <PermissionsListTab
            modules={modules}
            total={permissions.length}
            loading={permissionsLoading}
            onRetry={loadPermissions}
          />
        </TabsContent>
      </Tabs>

      <Dialog
        open={dialogUser !== null}
        onOpenChange={open => {
          if (!open) {
            setDialogUser(null);
          }
        }}
      >
        <DialogContent className="flex max-h-[92vh] w-[calc(100vw-2rem)] max-w-5xl flex-col gap-0 overflow-hidden rounded-2xl bg-background p-0">
          <DialogHeader className="px-6 pb-2 pt-6 text-left">
            <DialogTitle className="text-xl font-bold">
              Assign Direct Permissions
            </DialogTitle>
            <DialogDescription className="font-semibold text-foreground">
              {dialogUser ? getUserName(dialogUser) : ""}
              {dialogUser?.email && (
                <span className="ml-2 font-normal text-muted-foreground">
                  {dialogUser.email}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          {dialogUser && (
            <PermissionAssigner
              key={dialogUser.id}
              modules={modules}
              loading={permissionsLoading}
              initialIds={getUserPermissionIds(dialogUser)}
              onRetry={loadPermissions}
              onSubmit={async ids => {
                const ok = await assignPermissions(dialogUser, ids);
                if (ok) {
                  setDialogUser(null);
                }
                return ok;
              }}
              onCancel={() => setDialogUser(null)}
              bodyClassName="min-h-0 flex-1"
            />
          )}
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}

/* ------------------------------ Tab 1: Users ------------------------------ */

function UsersTab({
  users,
  loading,
  onAssign,
}: {
  users: AdminUserRecord[];
  loading: boolean;
  onAssign: (user: AdminUserRecord) => void;
}) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const filtered = useMemo(
    () => users.filter(u => matchesUser(u, search)),
    [users, search]
  );

  useEffect(() => {
    setPage(1);
  }, [search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const rows = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder="Search name, email, phone..."
        />
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Users className="h-4 w-4 text-primary" />
          <span>
            <span className="font-semibold text-foreground">
              {filtered.length}
            </span>{" "}
            users
          </span>
        </div>
      </div>

      <div className="-mx-3 overflow-x-auto rounded-2xl border border-border bg-card shadow-sm sm:mx-0">
        <Table className="[&_td]:border-border/70 min-w-[860px] border-collapse [&_td]:border [&_th]:border [&_th]:border-border">
          <TableHeader className="bg-muted/60">
            <TableRow>
              <TableHead className={cn(TH, "w-12")}>#</TableHead>
              <TableHead className={TH}>Name</TableHead>
              <TableHead className={TH}>Email</TableHead>
              <TableHead className={TH}>Phone</TableHead>
              <TableHead className={TH}>User Type</TableHead>
              <TableHead className={TH}>Joined On</TableHead>
              <TableHead className={TH}>Status</TableHead>
              <TableHead className={cn(TH, "w-52 text-right")}>
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading || rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="py-10 text-center text-muted-foreground"
                >
                  {loading ? "Loading…" : "No users found."}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((user, index) => (
                <TableRow
                  key={user.id}
                  className={index % 2 === 0 ? "bg-background" : "bg-muted/20"}
                >
                  <TableCell className="px-3 py-2.5 text-muted-foreground">
                    {(page - 1) * pageSize + index + 1}
                  </TableCell>
                  <TableCell className="px-3 py-2.5">
                    <UserAvatar user={user} />
                  </TableCell>
                  <TableCell className="break-all px-3 py-2.5">
                    {user.email ?? "—"}
                  </TableCell>
                  <TableCell className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      {user.phone_number ?? "—"}
                      {user.phone_number && user.is_phone_verified && (
                        <ShieldCheck
                          className="h-3.5 w-3.5 text-emerald-600"
                          aria-label="Verified"
                        />
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="px-3 py-2.5">
                    <Badge
                      variant="outline"
                      className="border-primary/30 bg-primary/5 font-medium capitalize text-primary"
                    >
                      {(user.user_type ?? "user").replace(/_/g, " ")}
                    </Badge>
                  </TableCell>
                  <TableCell className="whitespace-nowrap px-3 py-2.5">
                    {formatDate(user.date_joined)}
                  </TableCell>
                  <TableCell className="px-3 py-2.5">
                    {user.is_active === false ? (
                      <Badge
                        variant="outline"
                        className="border-red-200 bg-red-50 text-red-600"
                      >
                        Inactive
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="border-emerald-200 bg-emerald-50 text-emerald-700"
                      >
                        Active
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="px-3 py-2.5 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-primary/30 hover:bg-primary/10 gap-1 rounded-xl text-primary hover:text-primary"
                      onClick={() => onAssign(user)}
                    >
                      <ShieldCheck className="h-4 w-4" />
                      Assign Permissions
                      {getUserPermissionIds(user).length > 0 && (
                        <span className="ml-1 rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground">
                          {getUserPermissionIds(user).length}
                        </span>
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <TablePagination
        page={page}
        pageSize={pageSize}
        totalCount={filtered.length}
        totalPages={totalPages}
        loading={loading}
        onPageChange={setPage}
        onPageSizeChange={size => {
          setPageSize(size);
          setPage(1);
        }}
      />
    </div>
  );
}

/* ------------- Tab 2 / dialog: module cards with checkbox lists ------------ */

function PermissionAssigner({
  modules,
  loading,
  initialIds,
  onRetry,
  onSubmit,
  onCancel,
  bodyClassName,
}: {
  modules: PermissionModule[];
  loading: boolean;
  initialIds: number[];
  onRetry: () => void;
  onSubmit: (ids: number[]) => Promise<boolean>;
  onCancel: () => void;
  bodyClassName?: string;
}) {
  const [selected, setSelected] = useState<Set<number>>(
    () => new Set(initialIds)
  );
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const visibleModules = useMemo(
    () => filterModules(modules, search),
    [modules, search]
  );
  const visibleIds = useMemo(
    () => visibleModules.flatMap(m => m.permissions.map(p => p.id)),
    [visibleModules]
  );

  const toggle = (ids: number[], checked: boolean) => {
    setSelected(prev => {
      const next = new Set(prev);
      ids.forEach(id => (checked ? next.add(id) : next.delete(id)));
      return next;
    });
  };

  const stateOf = (ids: number[]): boolean | "indeterminate" => {
    const on = ids.filter(id => selected.has(id)).length;
    if (on === 0) {
      return false;
    }
    return on === ids.length ? true : "indeterminate";
  };

  const handleSubmit = async () => {
    setSaving(true);
    await onSubmit([...selected].sort((a, b) => a - b));
    setSaving(false);
  };

  return (
    <>
      <div className="flex flex-col gap-3 border-b border-border px-6 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
          <h3 className="whitespace-nowrap text-base font-semibold text-foreground">
            Select Direct Permissions
          </h3>
          <SearchBox
            value={search}
            onChange={setSearch}
            placeholder="Search modules..."
            className="sm:w-64"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            variant="outline"
            className="border-primary/30 bg-primary/5 text-primary"
          >
            {selected.size} selected
          </Badge>
          <Button
            size="sm"
            variant="outline"
            className="rounded-xl"
            disabled={loading || visibleIds.length === 0}
            onClick={() => toggle(visibleIds, true)}
          >
            Select All
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="rounded-xl"
            disabled={loading || selected.size === 0}
            onClick={() =>
              search ? toggle(visibleIds, false) : setSelected(new Set())
            }
          >
            Clear All
          </Button>
        </div>
      </div>

      <div
        className={cn("bg-muted/20 overflow-y-auto px-6 py-4", bodyClassName)}
      >
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          Currently assigned permissions are pre-selected. Saving replaces this
          user&apos;s direct permissions with the ones selected here.
        </div>

        {loading ? (
          <CenterState>
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading permissions…
          </CenterState>
        ) : modules.length === 0 ? (
          <CenterState>
            No permissions available.
            <Button
              size="sm"
              variant="outline"
              className="rounded-xl"
              onClick={onRetry}
            >
              Retry
            </Button>
          </CenterState>
        ) : visibleModules.length === 0 ? (
          <CenterState>No modules match “{search}”.</CenterState>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {visibleModules.map(mod => {
              const ids = mod.permissions.map(p => p.id);
              return (
                <div
                  key={mod.key}
                  className="overflow-hidden rounded-xl border border-border bg-card shadow-sm"
                >
                  <label className="bg-muted/70 flex cursor-pointer items-center justify-between gap-2 border-b border-border px-4 py-2.5">
                    <span className="text-sm font-semibold text-foreground">
                      {mod.label}
                    </span>
                    <Checkbox
                      checked={stateOf(ids)}
                      onCheckedChange={v => toggle(ids, v === true)}
                      aria-label={`Select all ${mod.label} permissions`}
                    />
                  </label>
                  <div className="space-y-2.5 px-4 py-3">
                    {mod.permissions.map(p => (
                      <label
                        key={p.id}
                        className="flex cursor-pointer items-start gap-2.5 text-sm text-foreground"
                      >
                        <Checkbox
                          className="mt-0.5"
                          checked={selected.has(p.id)}
                          onCheckedChange={v => toggle([p.id], v === true)}
                        />
                        <span className="leading-snug">{p.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-border bg-background px-6 py-4">
        <Button
          variant="outline"
          className="rounded-xl"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          className="gap-1 rounded-xl"
          onClick={handleSubmit}
          disabled={saving || loading}
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          {saving ? "Assigning…" : "Assign Permissions"}
        </Button>
      </div>
    </>
  );
}

/* ----------------------- Tab 3: System permissions list -------------------- */

function PermissionsListTab({
  modules,
  total,
  loading,
  onRetry,
}: {
  modules: PermissionModule[];
  total: number;
  loading: boolean;
  onRetry: () => void;
}) {
  const [search, setSearch] = useState("");
  const visibleModules = useMemo(
    () => filterModules(modules, search),
    [modules, search]
  );

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">
            System Permissions
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {total} permissions across {modules.length} modules
          </p>
        </div>
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder="Search permission or codename..."
        />
      </div>

      {loading ? (
        <CenterState>
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading permissions…
        </CenterState>
      ) : modules.length === 0 ? (
        <CenterState>
          No permissions available.
          <Button
            size="sm"
            variant="outline"
            className="rounded-xl"
            onClick={onRetry}
          >
            Retry
          </Button>
        </CenterState>
      ) : visibleModules.length === 0 ? (
        <CenterState>No permissions match “{search}”.</CenterState>
      ) : (
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
          {visibleModules.map(mod => (
            <section key={mod.key}>
              <h3 className="mb-3 border-b border-border pb-2 !font-sans !text-xl text-foreground">
                {mod.label}
              </h3>
              <ul className="space-y-2">
                {mod.permissions.map(p => (
                  <li key={p.id} className="flex items-start gap-2.5 text-sm">
                    <Shield className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="text-foreground">
                      {p.name}{" "}
                      <span className="text-xs text-muted-foreground">
                        ({p.codename})
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
